<template>
  <div :class="containerClass">
    <page-header
      :title="$route.params.id ? 'Edit Assessment' : 'Add Assessment'"
      :container-class="null"
      :show-back="true"
      class="mb-32pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Assessment Type" label-for="quiz_type" label-class="form-label">
        <b-form-select
          id="quiz_type"
          v-model="quiz_type"
          :options="quizTypeOptions"
        />
      </b-form-group>
      <page-separator title="Basic Information" />
      <!-- Basic Information -->
      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input id="title" v-model="quiz.title" required />
      </b-form-group>

      <b-row>
        <b-col md="6">
          <b-form-group label="Max Attempts" label-for="max_attempts" description="0 for unlimited attempts">
            <b-form-input id="max_attempts" type="number" min="0" v-model.number="quiz.max_attempts" required />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Duration"
            label-for="duration_minutes"
            description="Leave empty for no time limit"
          >
            <time-picker
              v-model="quiz.duration_minutes"
              input-id="duration_minutes"
              :show-total-minutes="true"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Total Marks" label-for="total_marks">
            <b-form-input id="total_marks" type="number" min="0" v-model.number="quiz.total_marks" required />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Passing Percentage" label-for="passing_percentage">
            <b-input-group>
              <b-form-input
                id="passing_percentage"
                type="number"
                min="0"
                max="100"
                v-model.number="quiz.passing_percentage"
                required
              />
              <b-input-group-append>
                <span class="input-group-text">%</span>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="Total Weight" label-for="weightage">
            <b-form-input id="weightage" type="number" min="0" max="100" v-model.number="quiz.weightage" :disabled="quiz_type === 'mock'" required />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Program" label-for="program" label-class="form-label">
            <v-select
              id="program"
              class="form-control v-select-custom"
              label="title"
              v-model="quiz.program"
              :reduce="program => program.id"
              placeholder="Select Program"
              :options="allPrograms"
              :loading="areProgramsLoading"
            >
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Quiz Settings -->
      <page-separator title="Quiz Settings" />

      <div class="">
        <div class="settings-container">
          <!-- Display Options Section -->
          <div class="settings-section">
            <span class="section-title">
              Display Options
            </span>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">visibility</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Show correct answers</h3>
                  <p class="setting-description">Students can see correct answers after submission</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.show_answers }"
                @click="quiz.show_answers = !quiz.show_answers"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.show_answers">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">history</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Submission history</h3>
                  <p class="setting-description">Allow students to view their past submissions</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.show_submission_history }"
                @click="quiz.show_submission_history = !quiz.show_submission_history"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.show_submission_history">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Quiz Behavior Section -->
          <div class="settings-section">
            <span class="section-title">
              Quiz Behavior
            </span>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">shuffle</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Shuffle questions</h3>
                  <p class="setting-description">Randomize question order for each attempt</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.shuffle_questions }"
                @click="quiz.shuffle_questions = !quiz.shuffle_questions"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.shuffle_questions">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">do_not_disturb_on</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Negative marking</h3>
                  <p class="setting-description">Deduct points for incorrect answers</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.enable_negative_marking }"
                @click="quiz.enable_negative_marking = !quiz.enable_negative_marking"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.enable_negative_marking">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">label</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Enable option labels</h3>
                  <p class="setting-description">Show alphabetical labels (a, b, c, d, e) beside each multiple-choice option</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.enable_labels }"
                @click="quiz.enable_labels = !quiz.enable_labels"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.enable_labels">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <page-separator title="Assessment Questions" />

      <!-- Question Type Buttons -->
      <div class="quiz-questions-section">
        <div class="dropdown-container">
          <button
            class="add-question-btn"
            @click.stop="toggleDropdown"
            :class="{ active: isDropdownOpen }"
            type="button"
          >
            <svg class="plus-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            Add Question
            <svg class="chevron-icon" :class="{ rotated: isDropdownOpen }" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
            </svg>
          </button>

          <div class="dropdown-menu" :class="{ open: isDropdownOpen }">
            <div class="dropdown-item" @click="addQuestion('mcq')">
              <div class="question-type-badge mc">MC</div>
              <span class="question-type-text">Multiple Choice</span>
            </div>
            <div class="dropdown-item" @click="addQuestion('text')">
              <div class="question-type-badge tx">TX</div>
              <span class="question-type-text">Text Answer</span>
            </div>
            <div class="dropdown-item" @click="addQuestion('tf')">
              <div class="question-type-badge tf">TF</div>
              <span class="question-type-text">True/False</span>
            </div>
          </div>
        </div>

        <!-- Marks Summary Indicator -->
        <div v-if="questions.length > 0" class="marks-summary" :class="{ 'marks-mismatch': !marksMatch && quiz.total_marks > 0 }">
          <div class="marks-info">
            <span class="marks-label">Sum of Question Marks:</span>
            <span class="marks-value">{{ sumOfQuestionMarks }}</span>
            <span class="marks-separator">/</span>
            <span class="marks-total">{{ quiz.total_marks }}</span>
          </div>
          <div v-if="!marksMatch && quiz.total_marks > 0" class="marks-warning">
            <i class="fas fa-exclamation-triangle"></i>
            Marks must match Total Marks ({{ quiz.total_marks }})
          </div>
          <div v-else-if="marksMatch && quiz.total_marks > 0" class="marks-success">
            <i class="fas fa-check-circle"></i>
            Marks match!
          </div>
        </div>
      </div>
      <b-form-invalid-feedback :state="!$v.questions.required && $v.questions.$dirty ? false : null"
        >Please add at least one question.</b-form-invalid-feedback
      >
      <!-- Questions List -->
      <draggable
        v-model="questions"
        class="questions-list draggable-cursor"
        @end="updateQuestionOrder"
        handle=".drag-handle"
      >
        <!--  -->
        <div
          v-for="(question, index) in questions"
          :key="question.id"
          class="d-flex align-items-center justify-content-center"
        >
          <!-- Drag Handle -->
          <div class="drag-handle" style="cursor: move;">
            <i class="fas fa-bars icon-16pt mr-2"></i>
          </div>
          <div class="question-card" style="width: 100%">
            <!-- Question Header -->
            <div class="question-header">
              <div class="question-number-badge">
                <span class="question-number">{{ index + 1 }}</span>
                <span class="question-type-label" :class="getTypeClass(question.type)">
                  {{ getTypeLabel(question.type) }}
                </span>
              </div>
              <button @click="deleteQuestion(index)" class="delete-btn" aria-label="Delete question">
                <md-icon class="text-danger">delete</md-icon>
              </button>
            </div>

            <!-- Question Input -->
            <div class="form-group">
              <label class="form-label">Question</label>
              <input
                v-model="question.question_text"
                type="text"
                placeholder="Enter your question"
                class="form-input"
                required
              />
            </div>

            <!-- Question Image Upload -->
            <div class="form-group">
              <label class="form-label">Question Image (Optional)</label>
              <template v-if="!question.image_url">
                <b-form-file
                  :id="`question-image-${question.id}`"
                  placeholder="Select image for question (optional)"
                  browse-text="Browse"
                  @input="uploadQuestionImage(index, $event)"
                  :disabled="uploadingImages[index]"
                  accept="image/png, image/jpeg, image/jpg"
                />
                <small class="text-muted">Add an image to accompany this question</small>
              </template>

              <div v-else class="question-image-preview">
                <div class="d-flex justify-content-end mb-2">
                  <b-btn size="sm" variant="danger" @click.prevent="removeQuestionImage(index)">
                    <md-icon class="icon-16pt">delete</md-icon>
                    Remove
                  </b-btn>
                </div>
                <img :src="question.image_url" class="preview-image" alt="Question image" />
              </div>
              
              <div v-if="uploadingImages[index]" class="mt-2">
                <b-spinner small type="grow" /> Uploading image...
              </div>
            </div>
            <page-separator>
              <b-link v-b-toggle="`${question.id}-${index}`" class="accordion__toggle p-0">
                <span class="flex">Question Details</span>
                <md-icon class="accordion__toggle-icon">
                  {{ questionVisible[index] ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                </md-icon>
              </b-link>
            </page-separator>
            <b-collapse
              :id="`${question.id}-${index}`"
              v-model="questionVisible[index]"
              class="accordion__menu"
              accordion="registerOverTimeAccordionId"
              :visible="true"
              @show="questionVisible[index] = true"
              @hide="questionVisible[index] = false"
            >
              <!-- Points Input -->
              <div class="form-group">
                <label class="form-label">Marks</label>
                <input v-model.number="question.marks" type="number" min="1" class="form-input points-input" required />
              </div>

              <!-- Explanation -->
              <div class="form-group">
                <label class="form-label">Explanation</label>
                <textarea v-model="question.explanation" class="form-input"></textarea>
              </div>

              <!-- Multiple Choice Options -->
              <div v-if="question.type === 'mcq'" class="options-section">
                <label class="form-label"
                  >Options
                  <i
                    class="material-icons icon-16pt clickable-item text-muted"
                    v-b-popover.hover.top="'Mark the correct answer.'"
                  >
                    info
                  </i></label
                >
                <div class="options-list">
                  <div v-for="(option, optionIndex) in question.options" :key="optionIndex" class="option-item">
                    <input
                      :id="`q${question.id}-option${optionIndex}`"
                      :checked="option.is_correct"
                      @change="selectCorrectOption(index, optionIndex)"
                      type="radio"
                      :name="`question-${question.id}`"
                      class="option-radio mr-2"
                    />
                    <span v-if="quiz.enable_labels" class="option-label">{{ getAlphabetLabel(optionIndex) }})</span>
                    <input
                      v-model="option.option_text"
                      type="text"
                      required
                      :placeholder="`Option ${optionIndex + 1}`"
                      class="option-input"
                    />
                    <div class="option-actions">
                      <button
                        @click.stop="openExplanationModal(index, optionIndex, option.explanation)"
                        class="explanation-btn"
                        type="button"
                        :title="option.explanation ? 'Edit explanation' : 'Add explanation'"
                      >
                        <md-icon :class="{ 'text-primary': option.explanation }">info</md-icon>
                      </button>
                      <button
                        @click.stop="removeOption(index, optionIndex)"
                        class="remove-option-btn btn-danger"
                        :disabled="question.options.length <= 2"
                        aria-label="Remove option"
                      >
                        <md-icon class="text-danger">delete</md-icon>
                      </button>
                    </div>
                  </div>
                </div>
                <button @click="addOption(index)" class="add-option-btn">
                  + Add Option
                </button>
              </div>

              <!-- Text Answer -->
              <div v-if="question.type === 'text'" class="text-section">
                <div class="form-group">
                  <div v-for="(option, optionIndex) in question.options" :key="optionIndex">
                    <label class="form-label">Correct Answer</label>
                    <input
                      v-model="option.option_text"
                      type="text"
                      placeholder="Enter the correct answer"
                      class="form-input"
                      required
                    />
                  </div>
                </div>
              </div>

              <!-- True/False -->

              <div v-if="question.type === 'tf'" class="tf-section">
                <label class="form-label">Correct Answer</label>
                <div class="radio-group">
                  <div v-for="(option, optionIndex) in question.options" :key="optionIndex" class="option-item">
                    <label class="radio-label">
                      <input
                        :checked="option.is_correct"
                        @change="selectCorrectOption(index, optionIndex)"
                        type="radio"
                        :name="`tf-${question.id}`"
                        class="radio-input"
                      />
                      <span class="radio-text">{{ option.option_text }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
        </div>
      </draggable>

      <!-- Empty State -->
      <div v-if="questions.length === 0" class="empty-state">
        <p>No questions added yet. Click one of the buttons above to get started!</p>
      </div>

      <!-- Submit Button -->
      <b-btn variant="primary" :disabled="isLoading" type="submit" class="mt-4">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Create' }} Quiz</span>
      </b-btn>
    </b-form>

    <!-- Explanation Modal -->
    <b-modal
      v-model="showExplanationModal"
      title="Option Explanation"
      @ok="saveExplanation"
      @hidden="closeExplanationModal"
      ok-title="Save"
      cancel-title="Cancel"
    >
      <b-form-group label="Explanation" label-for="explanation-text">
        <b-form-textarea
          id="explanation-text"
          v-model="currentExplanation.text"
          placeholder="Enter explanation for this option"
          rows="4"
        ></b-form-textarea>
        <small class="text-muted">This explanation will be shown to students when reviewing their answers.</small>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import TimePicker from '@/components/TimePicker.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import draggable from 'vuedraggable';
import { maxValue, minValue, required } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';
import Page from '../../../components/Page.vue';

export default {
  components: {
    PageHeader,
    PageSeparator,
    TimePicker,
    draggable,
  },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Quiz' : 'Add Quiz',
      quiz: {
        title: '',
        max_attempts: 0,
        duration_minutes: null,
        total_marks: 0,
        passing_percentage: 50,
        show_answers: true,
        show_submission_history: true,
        shuffle_questions: false,
        enable_negative_marking: false,
        enable_labels: false,
        is_exam: false,
        is_mock_exam: false,
        weightage: 0,
        program: null,
      },
      questionVisible: [],
      isLoading: false,
      questions: [],
      questionIdCounter: 0,
      // For explanation modal
      showExplanationModal: false,
      currentExplanation: {
        questionIndex: null,
        optionIndex: null,
        text: '',
      },
      quiz_type: null,
      isEdit: false,
      dragging: false,
      isDropdownOpen: false,
      uploadingImages: {},
      allPrograms: [],
      areProgramsLoading: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    breadcrumb() {
      return [
        { text: 'Home', to: this.routes.home },
        { text: 'Assessments', to: { name: 'list-quizzes' } },
        {
          text: this.$route.params.id ? 'Edit' : 'Add',
          active: true,
        },
      ];
    },

    sumOfQuestionMarks() {
      return this.questions.reduce((sum, question) => {
        return sum + (Number(question.marks) || 0);
      }, 0);
    },

    marksMatch() {
      return this.sumOfQuestionMarks === this.quiz.total_marks;
    },

    quizTypeOptions() {
      return [
        { value: null, text: 'Assessment (Default)' },
        { value: 'exam', text: 'Final Exam' },
        { value: 'mock', text: 'Mock Exam' },
      ];
    },
  },

  watch: {
    quiz_type(newVal) {
      if (newVal === 'mock') {
        this.quiz.weightage = 0;
      }
    },
  },

  validations: {
    quiz: {
      title: { required },
      max_attempts: { required, minValue: minValue(0) },
      duration_minutes: { minValue: minValue(1) },
      total_marks: { required, minValue: minValue(0) },
      passing_percentage: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(100),
      },
    },
    questions: { required },
  },

  methods: {
    ...mapActions('quizzes', ['createQuiz', 'updateQuiz', 'getQuiz', 'getQuizUploadUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('program', ['getAllPrograms']),

    updateQuestionOrder() {
      // Update the order field of each question based on their new position
      this.questions = this.questions.map((question, index) => ({
        ...question,
        order: index + 1,
      }));
    },
    addQuestion(type) {
      this.isDropdownOpen = false;
      const newQuestion = {
        id: this.questionIdCounter++,
        type: type,
        question_text: '',
        marks: 1,
        explanation: '',
        order: this.questions.length + 1,
        image_url: null,
        options:
          type === 'mcq'
            ? [
                { option_text: '', is_correct: true, explanation: '' },
                { option_text: '', is_correct: false, explanation: '' },
                { option_text: '', is_correct: false, explanation: '' },
                { option_text: '', is_correct: false, explanation: '' },
              ]
            : type === 'tf'
            ? [
                { option_text: 'True', is_correct: true, explanation: '' },
                { option_text: 'False', is_correct: false, explanation: '' },
              ]
            : [{ option_text: '', is_correct: true, explanation: '' }],
      };
      this.questions.push(newQuestion);
      this.questionVisible.push(true);
      // Scroll to the new question
      this.$nextTick(() => {
        const questionCards = document.querySelectorAll('.question-card');
        const lastCard = questionCards[questionCards.length - 1];
        if (lastCard) {
          lastCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    deleteQuestion(index) {
      this.questions.splice(index, 1);
    },

    openExplanationModal(questionIndex, optionIndex, currentExplanation = '') {
      this.currentExplanation = {
        questionIndex,
        optionIndex,
        text: currentExplanation || '',
      };
      this.showExplanationModal = true;
    },

    closeExplanationModal() {
      this.showExplanationModal = false;
      this.currentExplanation = {
        questionIndex: null,
        optionIndex: null,
        text: '',
      };
    },

    saveExplanation() {
      const { questionIndex, optionIndex, text } = this.currentExplanation;
      if (questionIndex !== null && optionIndex !== null) {
        this.$set(this.questions[questionIndex].options[optionIndex], 'explanation', text.trim());
      }
      this.closeExplanationModal();
    },

    addOption(questionIndex) {
      this.questions[questionIndex].options.push({ option_text: '', is_correct: false, explanation: '' });
    },

    removeOption(questionIndex, optionIndex) {
      const question = this.questions[questionIndex];
      if (question.options.length > 2) {
        question.options.splice(optionIndex, 1);
        // Adjust correct answer if needed
        if (question.options.filter(opt => opt.is_correct).length === 0) {
          question.options[0].is_correct = true;
        }
      }
    },

    selectCorrectOption(questionIndex, optionIndex) {
      // Set all options to false first
      this.questions[questionIndex].options.forEach((opt, idx) => {
        opt.is_correct = idx === optionIndex;
      });
    },
    getTypeLabel(type) {
      const labels = {
        mcq: 'Multiple Choice',
        text: 'Text Answer',
        tf: 'True/False',
      };
      return labels[type] || type;
    },

    getTypeClass(type) {
      return `type-${type}`;
    },

    getAlphabetLabel(index) {
      return String.fromCharCode(97 + index); // 97 is ASCII code for 'a'
    },
    async onSubmit() {
      this.$v.$touch();

      console.log('Form validation state:', {
        invalid: this.$v.$invalid,
        questionsLength: this.questions.length,
        totalMarks: this.quiz.total_marks,
        sumOfQuestionMarks: this.sumOfQuestionMarks
      });

      if (!this.$v.$invalid) {
        // Validate that sum of question marks equals total marks
        const totalMarks = Number(this.quiz.total_marks) || 0;
        const questionMarksSum = Number(this.sumOfQuestionMarks) || 0;

        console.log('Marks comparison:', {
          totalMarks,
          questionMarksSum,
          match: totalMarks === questionMarksSum
        });

        if (this.questions.length > 0 && questionMarksSum !== totalMarks) {
          console.log('Marks validation failed - showing error');
          this.makeToast({
            variant: 'danger',
            msg: `Total marks mismatch! Sum of individual question marks (${questionMarksSum}) must equal Total Marks (${totalMarks}).`,
          });
          // Scroll to marks summary
          this.$nextTick(() => {
            const summaryElement = document.querySelector('.marks-summary');
            if (summaryElement) {
              summaryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
          return;
        }

        this.isLoading = true;
        try {
          const quizData = {
            ...this.quiz,
            is_exam: this.quiz_type === 'exam',
            is_mock_exam: this.quiz_type === 'mock',
          };

          if (this.$route.params.id) {
            await this.updateQuiz({
              id: this.$route.params.id,
              data: { ...quizData, questions: this.questions },
            });
            this.makeToast({ variant: 'success', msg: 'Quiz updated successfully' });
            this.$router.push({ name: 'list-quizzes' });
          } else {
            await this.createQuiz({ ...quizData, questions: this.questions });
            this.makeToast({ variant: 'success', msg: 'Quiz created successfully' });
            this.$router.push({ name: 'list-quizzes' });
          }
        } catch (error) {
          console.error('Error saving quiz:', error);
          this.makeToast({
            variant: 'danger',
            msg: error.response?.data?.message || 'Failed to save quiz. Please try again.',
          });
        } finally {
          this.isLoading = false;
        }
      }
    },

    async uploadQuestionImage(questionIndex, file) {
      if (!file) return;

      // Validate file
      if (file.size > 5000000) {
        this.makeToast({ variant: 'danger', msg: 'File size should not exceed 5MB' });
        return;
      }
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        this.makeToast({ variant: 'danger', msg: 'File type should be png, jpg, or jpeg' });
        return;
      }

      this.$set(this.uploadingImages, questionIndex, true);

      try {
        const urlResp = await this.getQuizUploadUrl({
          file_name: file.name,
          content_type: file.type,
          upload_type: 'question_images',
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: file,
        });
        this.$set(this.questions[questionIndex], 'image_url', urlResp.data.upload_url.split('?')[0]);
        this.makeToast({ variant: 'success', msg: 'Image uploaded successfully' });
      } catch (error) {
        console.error('Error uploading image:', error);
        this.makeToast({ variant: 'danger', msg: 'Failed to upload image. Please try again.' });
      } finally {
        this.$set(this.uploadingImages, questionIndex, false);
      }
    },

    removeQuestionImage(questionIndex) {
      this.$set(this.questions[questionIndex], 'image_url', null);
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },

    async loadData() {
      if (this.$route.params.id) {
        this.isEdit = true;
        try {
          const response = await this.getQuiz(this.$route.params.id);
          this.quiz = { ...response.data };
          if (response.data.program) {
            this.quiz.program = typeof response.data.program === 'object' ? response.data.program.id : response.data.program;
          }
          if (this.quiz.is_mock_exam) {
            this.quiz_type = 'mock';
          } else if (this.quiz.is_exam) {
            this.quiz_type = 'exam';
          } else {
            this.quiz_type = null;
          }
          this.questions = response.data.questions;
        } catch (error) {
          console.error('Error loading quiz:', error);
          this.makeToast({ variant: 'danger', msg: 'Failed to load quiz data' });
          this.$router.push({ name: 'list-quizzes' });
        }
      }
    },
  },

  async mounted() {
    await this.fetchPrograms();
    await this.loadData();
  },
};
</script>

<style scoped>
.card {
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
  padding: 0.75rem 1.25rem;
  font-weight: 600;
}

.form-group {
  margin-bottom: 1.5rem;
}

.container {
  max-width: 600px;
  margin: 0 auto;
}

.title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
  text-align: left;
}

.btn:hover {
  opacity: 0.8;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.question-number-badge {
  display: flex;
  align-items: center;
  gap: 12px;
}

.question-number {
  background: #6c757d;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
}

.question-type-label {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.type-mcq {
  background: #d4edda;
  color: #155724;
}

.type-text {
  background: #e2d9f3;
  color: #6f42c1;
}

.type-tf {
  background: #ffeaa7;
  color: #d68910;
}

.delete-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.delete-btn:hover {
  background: #f8d7da;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-input::placeholder {
  color: #adb5bd;
}

.points-input {
  width: 80px;
}

.options-section {
  margin-top: 20px;
}

.options-list {
  margin-bottom: 12px;
}

.option-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.option-actions {
  display: flex;
  margin-left: 8px;
}

.explanation-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  margin-right: 4px;
  opacity: 0.7;
  transition: all 0.2s;
}

.explanation-btn:hover {
  opacity: 1;
  color: #007bff;
}

.remove-option-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  transition: all 0.2s;
}

.remove-option-btn:hover:not(:disabled) {
  opacity: 1;
}

.remove-option-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.option-radio {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.option-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.option-input:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.remove-option-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;
}

.remove-option-btn:hover:not(:disabled) {
  background: #f8d7da;
}

.remove-option-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-option-btn {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 0;
}

.add-option-btn:hover {
  text-decoration: underline;
}

.radio-group {
  display: flex;
  gap: 24px;
  margin-top: 8px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #495057;
}

.radio-input {
  width: 16px;
  height: 16px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
  font-size: 16px;
}

.quiz-questions-section {
  margin-bottom: 3rem;
}

.main-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1.5rem 0;
  letter-spacing: -0.025em;
}

.dropdown-container {
  position: relative;
  display: inline-block;
}

.add-question-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #eff6ff;
  color: #3b82f6;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
}

.add-question-btn:hover {
  background: #dbeafe;
  border-color: #bfdbfe;
}

.add-question-btn.active {
  background: #dbeafe;
  border-color: #3b82f6;
}

.plus-icon {
  width: 1rem;
  height: 1rem;
}

.chevron-icon {
  width: 1rem;
  height: 1rem;
  margin-left: auto;
  transition: transform 0.2s ease;
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 50;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s ease;
  margin-top: 0.25rem;
}

.dropdown-menu.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.dropdown-item:hover {
  background: #f9fafb;
}

.question-type-badge {
  width: 2rem;
  height: 2rem;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
  /* opacity: 0.3; */
}

.question-type-badge.mc {
  --tw-bg-opacity: 1;
  background-color: rgb(220 252 231 / var(--tw-bg-opacity, 1));
  color: green;
}

.question-type-badge.tx {
  --tw-bg-opacity: 1;
  background-color: rgb(243 232 255 / var(--tw-bg-opacity, 1));
  color: purple;
}

.question-type-badge.tf {
  --tw-bg-opacity: 1;
  background-color: rgb(254 243 199 / var(--tw-bg-opacity, 1));
  color: orange;
}

.question-type-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.settings-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.settings-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
}

.setting-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex: 1;
}

.setting-icon {
  margin-top: 0.125rem;
}

.setting-info {
  flex: 1;
}

.setting-title {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.setting-description {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

.option-label {
  font-weight: 600;
  color: #374151;
  margin-right: 0.5rem;
  font-size: 0.9375rem;
  min-width: 1.5rem;
  display: inline-block;
}

.toggle-switch {
  position: relative;
  width: 48px;
  height: 24px;
  background-color: #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.toggle-switch.active {
  background-color: #3b82f6;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch.active .toggle-slider {
  transform: translateX(24px);
}

.toggle-checkmark {
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.toggle-switch.active .toggle-checkmark {
  opacity: 1;
}

@media (max-width: 768px) {
  .settings-container {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

/* Marks Summary Indicator */
.marks-summary {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.marks-summary.marks-mismatch {
  background: #fef2f2;
  border-color: #fecaca;
}

.marks-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
}

.marks-label {
  color: #6b7280;
  font-weight: 500;
}

.marks-value {
  font-weight: 700;
  font-size: 1.125rem;
  color: #374151;
}

.marks-separator {
  color: #9ca3af;
  font-weight: 600;
}

.marks-total {
  font-weight: 700;
  font-size: 1.125rem;
  color: #374151;
}

.marks-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 500;
}

.marks-warning i {
  font-size: 1rem;
}

.marks-success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #10b981;
  font-size: 0.875rem;
  font-weight: 500;
}

.marks-success i {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .marks-summary {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Question Image Preview */
.question-image-preview {
  margin-top: 0.5rem;
}

.preview-image {
  max-width: 100%;
  height: auto;
  max-height: 300px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  object-fit: contain;
}
</style>
